import {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState,
  lazy,
  Suspense,
} from "react";
import ViewportContainer from "./ViewportContainer";
import ControlPanel from "./ControlPanel";
import { useCornerstone } from "../hooks/useCornerstone";
import StatusBar from "./StatusBar";
import SegmentationFilters from "./SegmentationFilters";
import { enableLabelmapsForSagittalCoronal } from "../segmentation/segmentRenderer";
import { Types } from "@cornerstonejs/core";
import { Point3 } from "@cornerstonejs/core/dist/esm/types";
import { Spinner } from "react-bootstrap";

// Lazy load the editor toolbar for better code splitting
const EditorToolbar = lazy(() => import("./EditorToolbar"));

// Create interface for ref methods
export interface ViewerV2Handle {
  handleResize: () => void;
  segmentationManager: any; // Reference to the segmentation manager
  setActiveTool?: (tool: string) => void; // Method for editor mode
}

interface ViewerV2Props {
  case: any;
  editorMode?: boolean; // Flag to enable editor mode
  loadingSegmentation?: boolean; // Flag to show loading indicator during segmentation decompression
  onToolChange?: (tool: string) => void; // Callback when tool changes in editor mode
}

const ViewerV2 = forwardRef<ViewerV2Handle, ViewerV2Props>((props, ref) => {
  const element1Ref = useRef<HTMLDivElement>(null);
  const element2Ref = useRef<HTMLDivElement>(null);
  const element3Ref = useRef<HTMLDivElement>(null);
  const element4Ref = useRef<HTMLDivElement>(null); // 3D viewport

  const [worldCoordinates, setWorldCoordinates] = useState<number[] | null>(
    null,
  );

  // Add state for viewport visibility
  const [visibleViewports, setVisibleViewports] = useState({
    axial: true,
    sagittal: true,
    coronal: true,
  });

  const handleToggleViewport = (name: "axial" | "sagittal" | "coronal") => {
    setVisibleViewports((prev) => {
      const updated = { ...prev, [name]: !prev[name] };
      // Call resize after state update
      setTimeout(() => handleManualResize(), 0);
      return updated;
    });
  };

  // Use the custom hook to manage Cornerstone functionality

  const {
    dataSource,
    clipPlanePosition,
    windowWidth,
    windowLevel,
    handleClipPositionChange,
    handleWindowWidthChange,
    handleWindowLevelChange,
    handleZoom,
    handleManualResize,
    segmentationManager,
    toolGroup,
  } = useCornerstone({
    element1Ref,
    element2Ref,
    element3Ref,
    onWorldPosChange: setWorldCoordinates,
    caseId: props.case,
    editorMode: props.editorMode,
  });

  // Handle enabling labelmaps for sagittal and coronal views
  const handleEnableLabelmaps = useCallback(
    async (progressCallback: (progress: number) => void) => {
      try {
        await enableLabelmapsForSagittalCoronal(progressCallback);
      } catch (error) {
        console.error("Failed to enable labelmaps:", error);
        // Reset progress on error
        progressCallback(0);
      }
    },
    [],
  );

  // Convert worldCoordinates (number[] | null) to Types.Point3 | null for StatusBar
  const worldCoordinatesForStatusBar: Types.Point3 | null =
    worldCoordinates && worldCoordinates.length === 3
      ? (worldCoordinates as Point3)
      : null;

  // Handle tool changes in editor mode
  const handleToolChange = useCallback(
    (tool: string) => {
      if (props.onToolChange) {
        props.onToolChange(tool);
      }
      // TODO: Implement actual tool activation in Cornerstone
      console.log("Active tool changed to:", tool);
    },
    [props],
  );

  // Expose methods and objects to the parent component through the ref
  useImperativeHandle(
    ref,
    () => ({
      handleResize: () => {
        handleManualResize();
      },
      segmentationManager, // Expose the segmentation manager
      setActiveTool: (tool: string) => {
        handleToolChange(tool);
      },
    }),
    [handleManualResize, segmentationManager, handleToolChange],
  );

  // Use a ref to store the wheel handler function
  const wheelHandlerRef = useRef<((event: WheelEvent) => void) | null>(null);

  // Update the ref whenever handleZoom changes
  useEffect(() => {
    // Create the wheel handler and store it in the ref
    wheelHandlerRef.current = (event: WheelEvent) => {
      event.preventDefault();
      const direction = event.deltaY < 0 ? "in" : "out";
      // Use CT_AXIAL as default for wheel zoom, or you could determine which viewport the mouse is over
      handleZoom("CT_AXIAL", direction);
    };
  }, [handleZoom]);

  // Create a stable event handler that uses the ref
  const stableWheelHandler = useCallback((event: WheelEvent) => {
    if (wheelHandlerRef.current) {
      wheelHandlerRef.current(event);
    }
  }, []);

  // Set up and clean up wheel event listener in a useEffect - only runs once on mount/unmount
  useEffect(() => {
    const element = element4Ref.current;
    if (element) {
      element.addEventListener("wheel", stableWheelHandler, { passive: false });

      return () => {
        element.removeEventListener("wheel", stableWheelHandler);
      };
    }
  }, [stableWheelHandler]); // Add stableWheelHandler to dependency array

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Conditionally render toolbar based on mode */}
      {props.editorMode ? (
        // Editor mode: Show editor toolbar with lazy loading
        <Suspense
          fallback={
            <div
              style={{
                padding: "15px 20px",
                borderBottom: "1px solid var(--modal-header-border)",
                backgroundColor: "var(--sidebar-bg)",
                textAlign: "center",
              }}
            >
              <Spinner animation="border" size="sm" /> Loading editor tools...
            </div>
          }
        >
          <EditorToolbar
            segmentationManager={segmentationManager}
            toolGroup={toolGroup}
            onToolChange={handleToolChange}
            caseId={props.case}
            loadingSegmentation={props.loadingSegmentation}
          />
        </Suspense>
      ) : (
        // View mode: Show segmentation filters for student/teacher overview
        <SegmentationFilters
          segmentationManager={segmentationManager}
          onEnableLabelmaps={handleEnableLabelmaps}
        />
      )}

      <div style={{ display: "flex", flex: 1 }}>
        <div>
          <ControlPanel
            dataSource={dataSource}
            clipPlanePosition={clipPlanePosition}
            windowWidth={windowWidth}
            windowLevel={windowLevel}
            onClipPositionChange={handleClipPositionChange}
            onWindowWidthChange={handleWindowWidthChange}
            onWindowLevelChange={handleWindowLevelChange}
          />
        </div>

        <div style={{ flex: 1, height: "100%", position: "relative" }}>
          <ViewportContainer
            element1Ref={element1Ref}
            element2Ref={element2Ref}
            element3Ref={element3Ref}
            visibleViewports={visibleViewports}
            onZoom={handleZoom}
          />
          <StatusBar
            worldCoordinates={worldCoordinatesForStatusBar}
            windowWidth={windowWidth}
            windowLevel={windowLevel}
            visibleViewports={visibleViewports}
            onToggleViewport={handleToggleViewport}
          />
        </div>
      </div>
    </div>
  );
});

export default ViewerV2;
